import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getBrands } from '../../../api/rewards.api';
import ImageWithFallback from '../../../components/ImageWithFallback';
import { useResponsiveItemCount } from '../../../hooks/useResponsiveItemCount';
import RewardsHubComingSoonPins from './RewardsHubComingSoonPins';

type Tab = 'my-rewards' | 'explore';

const TABS: { id: Tab; label: string }[] = [
  { id: 'my-rewards', label: 'My Rewards' },
  { id: 'explore', label: 'Explore Best Deals' }
];

const RewardsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('my-rewards');

  const { data } = useQuery({
    queryKey: ['getRewardBrands'],
    queryFn: async () => getBrands()
  });

  const NUMBER_OF_BRANDS_TO_SHOW = useResponsiveItemCount();

  let entities: any[] = [];
  let displaySeeMoreButton = false;
  if (data?.success) {
    const myJoined = data.data?.myJoined || [];
    const participating = data.data?.participating || [];

    if (myJoined.length + participating.length > NUMBER_OF_BRANDS_TO_SHOW) {
      displaySeeMoreButton = true;
    }

    if (myJoined.length >= NUMBER_OF_BRANDS_TO_SHOW) {
      entities = myJoined.slice(0, NUMBER_OF_BRANDS_TO_SHOW);
    } else {
      entities = [...myJoined];
      const remainingCount = NUMBER_OF_BRANDS_TO_SHOW - myJoined.length;
      if (remainingCount > 0) {
        entities.push(...participating.slice(0, remainingCount));
      }
    }
  }

  return (
    <section className="mx-auto mt-8 flex w-full max-w-full flex-col gap-y-4 px-4 pb-6 sm:px-6 lg:px-8">
      <h2 className="text-lg font-bold text-gray-900">Rewards Hub</h2>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex-1 py-2 pb-2 text-center text-sm font-medium transition-colors ${
                isActive
                  ? 'brand-gradient-text'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {isActive && (
                <span className="brand-gradient-bg absolute bottom-0 left-0 h-0.5 w-full rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* My Rewards panel */}
      {activeTab === 'my-rewards' && (
        <div className="rounded-xl bg-gray-100 p-4">
          {entities?.length ? (
            <div className="grid grid-cols-4 grid-rows-2 gap-4 md:grid-cols-5 2xl:grid-cols-6 2xl:grid-rows-2">
              {entities.slice(0, NUMBER_OF_BRANDS_TO_SHOW).map((entity) => (
                <Link
                  key={entity.entity_id}
                  to={`/my-rewards-hub/${entity.entity_id}`}
                  className="flex flex-col items-center justify-start gap-y-2 rounded-lg p-2 transition-colors hover:bg-gray-200"
                >
                  <div className="relative size-16 rounded-2xl bg-white p-2 shadow-lg">
                    <ImageWithFallback
                      src={entity?.entity_logo_image || ''}
                      alt={entity?.entity_name || ''}
                      className="size-full rounded-2xl object-contain"
                    />
                    {entity.is_new && (
                      <div className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        NEW
                      </div>
                    )}
                  </div>
                  <p className="line-clamp-2 w-full break-words text-center text-xs font-normal leading-tight">
                    {entity.entity_name}
                  </p>
                </Link>
              ))}

              {displaySeeMoreButton && (
                <Link
                  to="/my-rewards-hub"
                  className="flex flex-col items-center justify-start gap-y-2 rounded-lg p-2 transition-colors hover:bg-gray-200"
                >
                  <div className="size-16 rounded-2xl bg-white p-2 shadow-lg">
                    <img
                      src="/assets/images/see-more.svg"
                      alt="See More"
                      className="size-full object-contain"
                    />
                  </div>
                  <p className="line-clamp-2 w-full break-words text-center text-xs font-normal leading-tight">
                    See More
                  </p>
                </Link>
              )}
            </div>
          ) : (
            <div className="flex min-h-[180px] items-center justify-center rounded-2xl bg-white p-8 text-center shadow-sm">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  You haven&apos;t spent at any brand yet.
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Start exploring rewards now to earn points and special offers.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Explore Best Deals panel */}
      {activeTab === 'explore' && <RewardsHubComingSoonPins />}
    </section>
  );
};

export default RewardsSection;
